import { useState, useEffect } from 'react';
import { Wifi, WifiOff, Download, Check } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { usePWA } from '@/hooks/usePWA';
import { InstallModal } from './InstallModal';

type PWAStatus = 'installed-online' | 'browser-online' | 'offline' | 'update-available';

export const PWAStatusBadge = () => {
  const { isInstalled, updateAvailable } = usePWA();
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [status, setStatus] = useState<PWAStatus>('browser-online');
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    if (!isOnline) {
      setStatus('offline');
    } else if (updateAvailable) {
      setStatus('update-available');
    } else if (isInstalled) {
      setStatus('installed-online');
    } else {
      setStatus('browser-online');
    }
  }, [isOnline, isInstalled, updateAvailable]);

  const handleClick = () => {
    setModalOpen(true);
  };

  const getStatusConfig = () => {
    switch (status) {
      case 'installed-online':
        return {
          icon: Check,
          color: 'bg-green-500',
          label: 'Installed & Online',
          description: 'App is installed and connected',
          clickable: false,
        };
      case 'browser-online':
        return {
          icon: Wifi,
          color: 'bg-yellow-500',
          label: 'Browser & Online',
          description: 'Click to see installation instructions',
          clickable: true,
        };
      case 'offline':
        return {
          icon: WifiOff,
          color: 'bg-red-500',
          label: 'Offline Mode',
          description: 'No internet connection',
          clickable: false,
        };
      case 'update-available':
        return {
          icon: Download,
          color: 'bg-blue-500',
          label: 'Update Available',
          description: 'Click to refresh and get the latest version',
          clickable: true,
        };
    }
  };

  const config = getStatusConfig();
  const Icon = config.icon;

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge 
            variant="outline" 
            className={`gap-1.5 border-border/50 bg-background/50 backdrop-blur-sm transition-transform cursor-pointer hover:bg-background/70 active:scale-95 ${
              status === 'update-available' ? 'animate-bounce' : ''
            }`}
            onClick={handleClick}
            role="button"
            aria-label={config.label}
          >
            <span className={`w-2 h-2 rounded-full ${config.color} ${
              config.clickable ? 'animate-pulse' : ''
            }`} />
            <Icon className="w-3 h-3 text-muted-foreground" />
          </Badge>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-[200px]">
          <div className="space-y-1">
            <p className="font-semibold text-xs">{config.label}</p>
            <p className="text-xs text-muted-foreground">{config.description}</p>
          </div>
        </TooltipContent>
      </Tooltip>
      
      <InstallModal open={modalOpen} onOpenChange={setModalOpen} isOnline={isOnline} />
    </>
  );
};
